// File: app/src/utils/workoutPlanUtils.js
// Shared constants + minimal tree lookup helpers. All live traversal has
// moved into the compiled-queue pipeline (workoutCompiler + WorkoutEngine).

import { get } from "lodash";
import {
  DEFAULT_REST_STYLE,
  DEFAULT_SESSION_PACE,
} from "../config/trainingPreferences";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const DEFAULT_USER_PROFILE = {
  fitnessLevel: "intermediate",
  trainingExperience: "beginner",
  primaryGoal: "General Fitness",
  targetMuscles: ["Pectoralis Major", "Lats", "Abs", "Quads"],
  avoidedAreas: [],
  birthYear: null,
  injuryNotes: "",
  // Session defaults
  timeAvailable: 45,
  workoutStyle: "mixed",
  // Off / Auto / On (issue #70). Auto lets the AI size the segment and skip it
  // on short sessions; On always adds one at the fixed duration below; Off never
  // adds one. The include*/duration fields below are derived from these for the
  // prompt builder — see resolveSegmentMode / segmentFlagsForMode.
  warmupMode: "auto",
  // Cool-downs default off: a genuine cool-down is a short return to baseline,
  // and the stretching people reach for instead belongs in its own Flexibility
  // block rather than tacked onto every session.
  cooldownMode: "off",
  includeWarmup: true,
  warmupDuration: 5,
  includeCooldown: false,
  cooldownDuration: 5,
  // On Auto, a warm-up/cool-down is only added when the session is at least this
  // many minutes (0 = always). Sized by the AI within the time budget.
  autoWarmupThreshold: 30,
  autoCooldownThreshold: 30,
  // On Auto, a length the AI should aim for. 0 = no preference, i.e. let it size
  // the segment freely. Distinct from warmupDuration, which is the exact length
  // used on On — so a "no preference" here can't blank out the On duration.
  warmupPreferredDuration: 0,
  cooldownPreferredDuration: 0,
  // How the athlete likes a session put together, across every style
  // (config/trainingPreferences.js). Defaults reproduce the original behaviour.
  restStyle: DEFAULT_REST_STYLE,
  sessionPace: DEFAULT_SESSION_PACE,
  trainingPreferences: "",
  // Session shapes hidden from Surprise me / What's next (utils/mixedStyle.js).
  excludedSessionShapes: [],
  // Days a week the user aims to train; null or 0 = not set, so the app uses
  // a programme's schedule or works it out from history (utils/weeklyTargets).
  weeklySessions: null,
  maxHeartRate: null, // bpm; null = estimated from age (#94)
  restingHeartRate: null, // bpm; null = 60
  bodyWeightKg: null, // kg; needed for the VO2max estimate (#194)
  weightUnit: null, // how weights show (#191): null or "kg" = kg, "lb"; always stored in kg
  hrZoneMethod: null, // null or "reserve" = % of heart-rate reserve, "max" = % of max
  // Weekly targets raised above the goal's defaults, e.g. { resistance: 3 }.
  weeklyTargetOverrides: {},
};

// ---------------------------------------------------------------------------
// Warm-up / cool-down mode (issue #70)
// ---------------------------------------------------------------------------

export const SEGMENT_MODES = ["off", "auto", "on"];

const isSegmentMode = (value) => SEGMENT_MODES.includes(value);

/**
 * Resolve a stored profile/plan-config to a tri-state segment mode.
 *
 * `segment` is "warmup" or "cooldown". An explicit mode wins; otherwise we fall
 * back to the legacy pair of flags, which never carried an "always on" state:
 * `autoWarmup === false` meant manual minutes, and `includeWarmup === false`
 * (or a zero duration) meant none.
 */
export const resolveSegmentMode = (config = {}, segment = "warmup") => {
  const suffix = segment === "cooldown" ? "Cooldown" : "Warmup";
  const explicit = config[`${segment}Mode`];
  if (isSegmentMode(explicit)) return explicit;

  const include = config[`include${suffix}`];
  const duration = config[`${suffix.toLowerCase()}Duration`];
  if (include === false || duration === 0) return "off";
  if (config[`auto${suffix}`] === false) return "on";
  return "auto";
};

/**
 * Expand a segment mode back into the flags the prompt builder consumes, so
 * every downstream caller keeps its existing contract.
 *
 * The duration is deliberately left untouched: `include* === false` is already
 * enough for the prompt builder to omit the segment, and keeping the stored
 * minutes means a user who flips Off and back to On gets their value again.
 */
export const segmentFlagsForMode = (mode, segment = "warmup") => {
  const suffix = segment === "cooldown" ? "Cooldown" : "Warmup";
  const resolved = isSegmentMode(mode) ? mode : "auto";
  return {
    [`${segment}Mode`]: resolved,
    [`auto${suffix}`]: resolved === "auto",
    [`include${suffix}`]: resolved !== "off",
  };
};

export const INITIAL_COACH_STATE = {
  status: "ready", // ready, planning, running, paused, finished
  currentNode: null,
  nextUpNode: null,
  path: [],
  loopCounters: {},
  timeLeftInPhase: 0,
  progress: [],
  canGoBack: false,
  isLast: false,
  totalSessionDuration: 0,
  elapsedSessionTime: 0,
  phaseStartElapsed: 0,
  setStartElapsed: 0,
  repPhaseCumulativeActual: 0,
};

// ---------------------------------------------------------------------------
// Tree lookup
// ---------------------------------------------------------------------------

// Finds a node within the workout plan using a lodash-style path array.
// Used by useWorkoutCoach when projecting engine state back onto the React
// coachState (progress rings, active-node label, etc.).
export const findNodeByPath = (plan, path) => {
  if (!plan) return null;
  if (path == null) return null;
  if (path.length === 0) return plan;
  return get(plan, path);
};

// ---------------------------------------------------------------------------
// FTMS equipment detection — resolves plan node.equipment strings to the
// canonical EquipmentType (matches utils/ftms/types.ts).
// ---------------------------------------------------------------------------

// Lower-cased aliases → canonical EquipmentType. Extend this map when a new
// machine type is wired in. Plans authored by LLMs are inconsistent ("rower"
// vs "row erg" vs "concept 2"), so accept the common variants.
export const EQUIPMENT_BY_NAME = {
  "assault bike": "indoor_bike",
  "spin bike": "indoor_bike",
  "indoor bike": "indoor_bike",
  bike: "indoor_bike",
  cycling: "indoor_bike",
  rower: "rower",
  rowing: "rower",
  "row erg": "rower",
  "concept 2": "rower",
  concept2: "rower",
  // Phase 2: treadmill, cross_trainer, step_climber, stair_climber.
};

/** Resolve a plan node's equipment string to an EquipmentType or null. */
export const equipmentTypeForNode = (node) => {
  if (!node) return null;
  const eq = (node.equipment || "").toLowerCase().trim();
  if (eq && EQUIPMENT_BY_NAME[eq]) return EQUIPMENT_BY_NAME[eq];
  if (/\b(bike|cycling|spin)\b/.test((node.label || "").toLowerCase())) {
    return "indoor_bike";
  }
  return null;
};

// Back-compat: isBikeNode is still consumed by older callers (e.g. ringSegment
// type detection). Treat anything that's an FTMS-driven cardio piece as a
// "bike" for those purposes — they really want "is this a power-stream node".
export const isBikeNode = (node) => equipmentTypeForNode(node) != null;

export const treeHasBike = (node) =>
  !!node &&
  (isBikeNode(node) || (node.children || []).some((c) => treeHasBike(c)));
